/**
 * Browser equivalents of the Flutter app's share / "save to Files" sheet.
 *
 * The Flutter app wrote a temp file and handed it to the platform share sheet
 * because app-private storage is invisible to a file manager. On the web the
 * user's Downloads folder plays that role, so an anchor with `download` is the
 * direct counterpart — with the File System Access API used when the browser
 * offers it, so the user picks the destination exactly as they do on mobile.
 */

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: { description: string; accept: Record<string, string[]> }[];
}

interface FileSystemWritable {
  write: (data: BufferSource | Blob | string) => Promise<void>;
  close: () => Promise<void>;
}

interface FileSystemFileHandleLike {
  createWritable: () => Promise<FileSystemWritable>;
}

type ShowSaveFilePicker = (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandleLike>;

function saveFilePicker(): ShowSaveFilePicker | null {
  const candidate = (window as unknown as { showSaveFilePicker?: ShowSaveFilePicker })
    .showSaveFilePicker;
  return typeof candidate === 'function' ? candidate : null;
}

export type SaveOutcome = 'saved' | 'downloaded' | 'cancelled';

/**
 * Offers `blob` to the user under `fileName`.
 *
 * Returns `cancelled` when the user dismissed the picker — the caller should
 * stay quiet in that case rather than reporting an error.
 */
export async function saveBlob(
  blob: Blob,
  fileName: string,
  accept?: Record<string, string[]>,
): Promise<SaveOutcome> {
  const picker = saveFilePicker();

  if (picker) {
    try {
      const handle = await picker({
        suggestedName: fileName,
        types: accept ? [{ description: fileName, accept }] : undefined,
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return 'saved';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
      // Any other failure (a sandboxed iframe, a policy block) falls through
      // to the anchor download, which works everywhere.
      console.warn('File picker unavailable, falling back to download:', error);
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}

export function saveBinary(bytes: ArrayBuffer, fileName: string): Promise<SaveOutcome> {
  return saveBlob(new Blob([bytes], { type: 'application/octet-stream' }), fileName, {
    'application/octet-stream': ['.bin'],
  });
}

export function saveText(text: string, fileName: string, mime = 'text/plain'): Promise<SaveOutcome> {
  const extension = fileName.slice(fileName.lastIndexOf('.'));
  return saveBlob(new Blob([text], { type: `${mime};charset=utf-8` }), fileName, {
    [mime]: [extension],
  });
}

/**
 * Copies `text` to the clipboard.
 *
 * The async Clipboard API needs a secure context; the legacy path keeps the
 * Copy button working on a plain-HTTP LAN deployment.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn('Clipboard API failed, trying the legacy path:', error);
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.append(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    return copied;
  } catch (error) {
    console.warn('Clipboard copy failed:', error);
    return false;
  }
}
